import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { Tooltip } from '../../../components/Tooltip';
import type { Company } from '../../../domain/company';
import { getFaviconUrl } from '../../../services/favicon';

import { StyledCompanyLogo, StyledMultipleDomainsDropdownOption } from './styles';
import { isCompanyDataPresent } from './utils';

type DomainOption = Pick<Company, 'domain' | 'yearMonth' | 'name'>;

type MeetingDropdownOptionProps = {
  option: DomainOption;
  onSelectDomain: (domain: string) => void;
  isSelected: boolean;
};

export const MultipleDomainsDropdownOption = ({ option, onSelectDomain, isSelected }: MeetingDropdownOptionProps) => {
  const { domain, name, yearMonth } = option;
  const { t } = useTranslation();
  const handleSelectedDomain = () => onSelectDomain(domain);

  const isDataPresent = useMemo(() => isCompanyDataPresent({ domain, name, yearMonth }), [domain, name, yearMonth]);

  return isDataPresent ? (
    <StyledMultipleDomainsDropdownOption
      key={domain}
      onClick={handleSelectedDomain}
      isSelected={isSelected}
      data-automation="MeetingDdOption"
    >
      <StyledCompanyLogo data-automation="MeetingDdOptionLogo">
        <img src={getFaviconUrl(domain, 14)} width="14px" height="14px" alt={name} />
      </StyledCompanyLogo>

      {name}
    </StyledMultipleDomainsDropdownOption>
  ) : (
    <Tooltip key={domain} content={t('meeting.unmatched_company_tooltip')} placement="top" minWidth={153}>
      <StyledMultipleDomainsDropdownOption isDisabled data-automation="MeetingDdOptionDisabled">
        <StyledCompanyLogo>
          <SWReactIcons iconName="globe" size="xs" />
        </StyledCompanyLogo>
        {domain}
      </StyledMultipleDomainsDropdownOption>
    </Tooltip>
  );
};
